import { type FC, useMemo } from 'react';
import { Empty, Radio, Spin } from 'antd';

import SearchBox from 'components/molecules/SearchBox';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';

import type { ImportEventSourceCategory } from '../../../config/importEventFromPlanConfig';
import {
    TPO_IMPORT_EVENTS_LOADING,
    TPO_IMPORT_EVENTS_MODAL,
    TPO_IMPORT_EVENTS_PLAN_SELECT,
    TPO_IMPORT_EVENTS_SEARCH_INPUT,
    tpoOptionTestId,
} from '../../../config/testIds';

const PLAN_SELECT_STYLE = { width: 240 };

const PLAN_SELECT_LOADING = (
    <div className="flex justify-center p-3" data-testid={TPO_IMPORT_EVENTS_LOADING}>
        <Spin size="small" />
    </div>
);

const PLAN_SELECT_EMPTY = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No plans available" />;

export interface ImportEventPlanOption {
    label: string;
    value: string;
}

export interface ImportEventSourceToolbarProps {
    sourceLabels: Record<string, string>;
    availableCategories: ImportEventSourceCategory[];
    selectedSource: ImportEventSourceCategory;
    onSourceChange: (category: ImportEventSourceCategory) => void;
    planOptions: ImportEventPlanOption[];
    sourcePlanId: string | null;
    onPlanChange: (planId: string) => void;
    plansLoading: boolean;
    plansHasMore: boolean;
    onPlansScrollEnd: () => void;
    onPlanSearch: (value: string) => void;
    eventSearch: string;
    onEventSearchChange: (value: string) => void;
    /** Receives the submitted input value from AntD's search box. */
    onEventSearchSubmit: (value: string) => void;
}

/** Source-plan pickers and the event search box above the Import-Events table. */
const ImportEventSourceToolbar: FC<ImportEventSourceToolbarProps> = ({
    sourceLabels,
    availableCategories,
    selectedSource,
    onSourceChange,
    planOptions,
    sourcePlanId,
    onPlanChange,
    plansLoading,
    plansHasMore,
    onPlansScrollEnd,
    onPlanSearch,
    eventSearch,
    onEventSearchChange,
    onEventSearchSubmit,
}) => {
    const optionsWithTestIds = useMemo(
        () =>
            planOptions.map((option) => ({
                ...option,
                'data-testid': tpoOptionTestId(TPO_IMPORT_EVENTS_PLAN_SELECT, option.value),
            })),
        [planOptions],
    );

    return (
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
                <span className="text-sm text-[#18181b]">Import from:</span>
                <Radio.Group value={selectedSource} onChange={(e) => onSourceChange(e.target.value)}>
                    {availableCategories.map((cat) => (
                        <Radio key={cat} value={cat} data-testid={tpoOptionTestId(TPO_IMPORT_EVENTS_MODAL, cat)}>
                            {sourceLabels[cat] ?? cat}
                            {cat !== 'self' ? ':' : ''}
                        </Radio>
                    ))}
                </Radio.Group>
                <SelectWithInfiniteScroller
                    style={PLAN_SELECT_STYLE}
                    placeholder="Select Plan"
                    value={sourcePlanId ?? undefined}
                    options={optionsWithTestIds}
                    data-testid={TPO_IMPORT_EVENTS_PLAN_SELECT}
                    onChange={onPlanChange}
                    loading={plansLoading}
                    hasMore={plansHasMore}
                    onInfiniteScrollOnNextMemoised={onPlansScrollEnd}
                    showSearch
                    filterOption={false}
                    onSearch={onPlanSearch}
                    notFoundContent={plansLoading ? PLAN_SELECT_LOADING : PLAN_SELECT_EMPTY}
                />
            </div>
            <SearchBox
                search={eventSearch}
                setSearch={onEventSearchChange}
                onSearch={onEventSearchSubmit}
                data-testid={TPO_IMPORT_EVENTS_SEARCH_INPUT}
            />
        </div>
    );
};

ImportEventSourceToolbar.displayName = 'ImportEventSourceToolbar';
export default ImportEventSourceToolbar;
